import { LocalSessionKey } from 'app/constants/app-constants';
import { LocalStorage } from 'app/utils/storage';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getCustomerBillHistory,
  exportCustomerBillHistory,
} from 'app/api/customer';
import { startExportPoll, triggerDownload } from 'app/utils/exportPoller';
import Message from 'app/utils/Message';
import Toast from 'app/utils/Toast';

const dataPerPage = 10;

// Hook for the "Bill History" tab of customer detail.
//
// When the customer has a deletion log, the tab renders two independently
// paginated tables ("Before account deletion" and "After account restoration").
// Each section drives its own API call with `bucket=before|after`, so the
// pagination state and the row counts shown under each table belong to that
// section alone and not to a global list. When the customer was never deleted,
// only `flatData` is used and the bucket states stay empty.
const useCustomerBillHistory = (latestDeletionDate, latestRestorationDate) => {
  const customerData = JSON.parse(
    LocalStorage.get(LocalSessionKey.CUSTOMER_DATA)
  );

  const [pageLoading, setPageLoading] = useState(false);

  const [flatData, setFlatData] = useState([]);
  const [flatTotal, setFlatTotal] = useState(0);
  const [flatPageCount, setFlatPageCount] = useState(1);

  const [beforeData, setBeforeData] = useState([]);
  const [beforeTotal, setBeforeTotal] = useState(0);
  const [beforePageCount, setBeforePageCount] = useState(1);

  const [afterData, setAfterData] = useState([]);
  const [afterTotal, setAfterTotal] = useState(0);
  const [afterPageCount, setAfterPageCount] = useState(1);

  const [downloadInProgress, setDownloadInProgress] = useState(false);
  const downloadInProgressRef = useRef(false);

  const fetchSection = useCallback(
    async (page, bucket) => {
      setPageLoading(true);
      try {
        const response = await getCustomerBillHistory(
          customerData?.id,
          page,
          dataPerPage,
          bucket,
          latestDeletionDate,
          latestRestorationDate
        );
        if (response?.responseCode === 200) {
          const rows = response?.data?.data || [];
          const count = response?.data?.count || 0;
          const pageCount = Math.ceil(count / dataPerPage) || 1;
          if (bucket === 'before') {
            setBeforeData(rows);
            setBeforeTotal(count);
            setBeforePageCount(pageCount);
          } else if (bucket === 'after') {
            setAfterData(rows);
            setAfterTotal(count);
            setAfterPageCount(pageCount);
          } else {
            setFlatData(rows);
            setFlatTotal(count);
            setFlatPageCount(pageCount);
          }
        } else {
          Toast.error(
            response?.response?.message || Message.Error.COMMON_MESSAGE
          );
        }
      } catch (error) {
        Toast.error(Message.Error.COMMON_MESSAGE);
      } finally {
        setPageLoading(false);
      }
    },
    [customerData?.id, latestDeletionDate, latestRestorationDate]
  );

  const fetchFlat = useCallback((page) => fetchSection(page, null), [fetchSection]);
  const fetchBefore = useCallback((page) => fetchSection(page, 'before'), [fetchSection]);
  const fetchAfter = useCallback((page) => fetchSection(page, 'after'), [fetchSection]);

  // Async Excel export of the customer's full bill history. Returns a jobId,
  // then polls /export/status/:jobId and downloads when ready — identical flow
  // to the reports export.
  const startDownload = useCallback(
    async (downloadType = 'excel') => {
    if (downloadInProgressRef.current) return;
    downloadInProgressRef.current = true;
    setDownloadInProgress(true);
    const finish = () => {
      downloadInProgressRef.current = false;
      setDownloadInProgress(false);
    };
    try {
      const response = await exportCustomerBillHistory(
        customerData?.id,
        downloadType,
        null,
        latestDeletionDate,
        latestRestorationDate
      );
      const jobId = response?.data?.jobId;
      if (response?.responseCode !== 200 || !jobId) {
        Toast.error(response?.response?.message || Message.Error.COMMON_MESSAGE);
        finish();
        return;
      }
      startExportPoll(jobId, {
        onDone: (url) => {
          triggerDownload(url);
          finish();
        },
        onFailed: () => {
          Toast.error(Message.Error.COMMON_MESSAGE);
          finish();
        },
        onError: () => {
          Toast.error(Message.Error.COMMON_MESSAGE);
          finish();
        },
      });
    } catch (error) {
      Toast.error(Message.Error.COMMON_MESSAGE);
      finish();
    }
  }, [customerData?.id, latestDeletionDate, latestRestorationDate]);

  useEffect(() => {
    if (!latestDeletionDate) {
      fetchFlat(1);
      return;
    }
    fetchBefore(1);
    if (latestRestorationDate) {
      fetchAfter(1);
    }
  }, [latestDeletionDate, latestRestorationDate, fetchFlat, fetchBefore, fetchAfter]);

  return {
    pageLoading,

    flatData,
    flatTotal,
    flatPageCount,
    fetchFlat,

    beforeData,
    beforeTotal,
    beforePageCount,
    fetchBefore,

    afterData,
    afterTotal,
    afterPageCount,
    fetchAfter,

    dataPerPage,

    startDownload,
    downloadInProgress,
  };
};

export default useCustomerBillHistory;
